import { useState, useMemo, useEffect } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CheckSquare,
  Package,
  BarChart3,
  Settings,
  LogOut,
  User,
  Users,
  Clock,
  Phone,
  Scan,
  ChevronUp,
  ChevronRight,
  Receipt,
  Calendar,
  IndianRupee,
  DollarSign,
  FileText,
  ClipboardCheck,
  FolderOpen,
  ClipboardList,
  BookOpen,
  Smile,
  Stethoscope,
  Syringe,
  Scissors,
  Crown,
  Heart,
  Info,
  FlaskConical,
  Boxes,
  BriefcaseMedical,
  Pill,
  Baby,
  Bone,
  ShieldCheck,
  Wallet,
  Building2,
  Shield,
  Wrench,
  Mail,
  Eye,
  FileCheck,
  Scale,
  Gift,
  Calculator,
  Bell,
  Camera,
  LifeBuoy,
  Megaphone,
  Car,
  GraduationCap,
  Inbox,
  Lock,
  MessageSquare,
  Bug,
} from "lucide-react";
import { DentalFlowLogo } from "@/components/DentalFlowLogo";
import { NotificationDropdown } from "@/components/notifications/NotificationDropdown";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useStandardRole } from "@/hooks/useStandardRole";
import { useModulePermissions } from "@/hooks/useModulePermissions";
import { usePrivilegedPageAccess } from "@/hooks/usePrivilegedPageAccess";
import { useUserClinicIds } from "@/hooks/useUserClinics";
import {
  applyPermissionCeiling,
  getClinicRolesForAccessCeiling,
  getEffectivePermissions,
  isModuleKey,
  type ModulePermission as RoleModulePermission,
} from "@/lib/role-permissions";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useModuleFeatureAccess } from "@/hooks/useTenantFeatureAccess";
import { SidebarFeatureItem } from "@/components/SidebarFeatureItem";
import { usePendingApprovals } from "@/hooks/useApprovalRequests";
import { useBugReportDialog } from "@/components/bug-report/BugReportProvider";
import { RECALLS_FOLLOWUPS_PAUSED } from "@/config/recall-followup-availability";
import { COMMUNICATION_UI_HIDDEN } from "@/config/communication-availability";

interface SidebarProfile {
  full_name?: string | null;
}

interface AppSidebarProps {
  profile?: SidebarProfile;
  onSignOut: () => void;
}

interface NavItem {
  title: string;
  url: string;
  icon: React.ElementType;
  show: boolean;
  featureCode?: string;
  requiredPlan?: "Professional" | "Enterprise";
  badge?: number;
}

interface NavCategory {
  id: string;
  title: string;
  icon: React.ElementType;
  items: NavItem[];
}

/** Wrapper that checks feature access and renders locked or normal sidebar item */
function SidebarFeatureItemWrapper({ item, isActive }: { item: NavItem; isActive: boolean }) {
  const { hasModuleFeature, isLoading } = useModuleFeatureAccess(item.featureCode || "");

  // If loading or feature enabled, render normal item
  if (isLoading || hasModuleFeature) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          size="default"
          className={`h-9 text-sm transition-all ${
            isActive 
              ? 'bg-sidebar-primary/10 text-sidebar-primary font-medium border-l-2 border-sidebar-primary -ml-[13px] pl-[11px]' 
              : 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/30'
          }`}
        >
          <NavLink to={item.url}>
            <span>{item.title}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  // Feature is locked
  return (
    <SidebarFeatureItem
      title={item.title}
      url={item.url}
      featureCode={item.featureCode!}
      requiredPlan={item.requiredPlan!}
      isActive={isActive}
    />
  );
}

export function AppSidebar({ profile, onSignOut }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  
  // Standard role hook — the single source of truth
  const { data: standardRole, isLoading: roleLoading } = useStandardRole();
  const { data: modulePermissions, isLoading: permissionsLoading } = useModulePermissions();
  const privilegedAccess = usePrivilegedPageAccess();
  const { clinics: userClinics } = useUserClinicIds();
  // Pending approvals count for badge
  const { data: pendingApprovals } = usePendingApprovals();
  const { openReportDialog } = useBugReportDialog();

  // Helper to check if a route is active
  const isActiveRoute = (url: string): boolean => {
    if (url === "/") return location.pathname === "/";
    
    // Handle settings section query params
    if (url.startsWith("/admin/settings?section=")) {
      const urlSection = new URLSearchParams(url.split("?")[1]).get("section");
      const currentSection = new URLSearchParams(location.search).get("section");
      return location.pathname === "/admin/settings" && urlSection === currentSection;
    }
    
    // For /admin/settings without query param, only match if no section param is present
    if (url === "/admin/settings") {
      return location.pathname === "/admin/settings" && !location.search.includes("section=");
    }
    
    // Exact match
    if (location.pathname === url) return true;
    
    // For /billing, don't match if on /billing/collections (separate nav item)
    if (url === "/billing" && location.pathname.startsWith("/billing/collections")) {
      return false;
    }
    // For /appointments, don't match if on /appointments/requests (separate nav item)
    if (url === "/appointments" && location.pathname.startsWith("/appointments/requests")) {
      return false;
    }
    return location.pathname.startsWith(url + "/");
  };

  // Helper function to get display role
  const getDisplayRole = () => {
    if (roleLoading) return "Loading…";

    if (standardRole?.isSuperAdmin) return "Super Admin";
    if (standardRole?.isOwner) return "Owner";
    if (standardRole?.isManager && standardRole?.isDoctor) return "Clinic Incharge";
    if (standardRole?.isManager && standardRole?.hasAuditMode) return "QC Manager";
    if (standardRole?.isManager) return "Manager";
    if (standardRole?.isDoctor) return "Doctor";
    if (standardRole?.isFrontDesk) return "Front Desk";
    if (standardRole?.isAssistant) return "Assistant";

    return "Staff";
  };

  // Define navigation categories using standard roles only
  const navigationCategories = useMemo<NavCategory[]>(() => {
    // Standard role flags
    const isOwner = standardRole?.isOwner || false;
    const isManager = standardRole?.isManager || false;
    const isSuperAdmin = standardRole?.isSuperAdmin || false;

    // Access levels
    const hasFullAccess = isOwner || isSuperAdmin;
    const hasManagerAccess = hasFullAccess || isManager;
    const accessRoleAssignments = standardRole?.assignments?.length
      ? standardRole.assignments.map((assignment) => ({
          standardRole: assignment.standardRole,
          legacyRole: assignment.legacyRole,
        }))
      : (userClinics ?? []).map((clinic) => ({
          legacyRole: clinic.role,
        }));
    const clinicRoles = getClinicRolesForAccessCeiling(accessRoleAssignments);
    const roleCeiling = getEffectivePermissions(hasFullAccess ? "admin" : "staff", clinicRoles);
    const canViewModule = (module: keyof NonNullable<typeof modulePermissions>) => {
      if (hasFullAccess) return true;
      const permission = modulePermissions?.[module];
      if (!isModuleKey(module)) {
        return Boolean(permission?.can_view);
      }

      return applyPermissionCeiling(
        permission as RoleModulePermission | undefined,
        roleCeiling[module]
      ).can_view;
    };

    // Pending approvals badge count
    const approvalCount = pendingApprovals?.length || 0;

    return [
      {
        id: "overview",
        title: "Overview",
        icon: LayoutDashboard,
        items: [
          { title: "Dashboard", url: "/", icon: LayoutDashboard, show: true },
        ],
      },
      {
        id: "patient-care",
        title: "Patient Care",
        icon: Users,
        items: [
          { title: "Patients", url: "/patients", icon: Users, show: canViewModule("patients") },
          { title: "Clinical Visits", url: "/clinical-visits", icon: Stethoscope, show: canViewModule("clinical_visits") },
          { title: "Appointments", url: "/appointments", icon: Calendar, show: canViewModule("appointments") },
          { title: "Booking Requests", url: "/appointments/requests", icon: Inbox, show: canViewModule("appointments") },
          { title: "Prescriptions", url: "/prescriptions", icon: Pill, show: canViewModule("prescriptions") },
          { title: "Treatment Plans", url: "/treatment-plans", icon: ClipboardList, show: privilegedAccess.canViewTreatmentPlans && canViewModule("treatment_planning") },
          { title: "Recalls & Follow-ups", url: "/recalls", icon: Bell, show: !RECALLS_FOLLOWUPS_PAUSED && canViewModule("clinical_visits") },
          { title: "Communication", url: "/communications", icon: MessageSquare, show: !COMMUNICATION_UI_HIDDEN && canViewModule("clinical_visits") },
        ],
      },
      // DEPRECATED: Specialty Cases module removed in Phase 24.
      // All specialty tracking is now handled via Treatment Plans + Treatment Tasks.
      // Routes are redirected to /treatment-plans for backward compatibility.
      {
        id: "operations",
        title: "Operations",
        icon: Package,
        items: [
          // Everyone gets the team directory; only owners/managers see the
          // Add Staff action inside it.
          { title: "Staff", url: "/staff", icon: Users, show: true },
          { title: "Lab Work", url: "/lab-work", icon: FlaskConical, show: canViewModule("cases") },
          { title: "Tasks", url: "/tasks", icon: CheckSquare, show: canViewModule("tasks") },
          { title: "Inventory", url: "/inventory", icon: Boxes, show: canViewModule("inventory") },
          { title: "Imaging Queue", url: "/imaging-queue", icon: Camera, show: canViewModule("radiology"), featureCode: "radiology", requiredPlan: "Professional" },
          { title: "Radiology", url: "/radiology", icon: Scan, show: canViewModule("radiology"), featureCode: "radiology", requiredPlan: "Professional" },
          { title: "Sterilization", url: "/compliance/sterilization", icon: ShieldCheck, show: canViewModule("clinical_visits") },
        ],
      },
      {
        id: "documents",
        title: "Documents",
        icon: FolderOpen,
        items: [
          { title: "Consent Forms", url: "/documents/consent-forms", icon: ClipboardList, show: canViewModule("consent_forms"), featureCode: "digital_consent", requiredPlan: "Professional" },
          { title: "Medical Records", url: "/documents/medical-records", icon: FileText, show: canViewModule("medical_records") },
          { title: "Post-Care Instructions", url: "/documents/post-care", icon: Heart, show: canViewModule("post_care_instructions") },
          { title: "Legal Documents", url: "/documents/legal", icon: Scale, show: canViewModule("legal_documents") },
          { title: "SOP", url: "/documents/sop", icon: BookOpen, show: canViewModule("documents") },
          { title: "Learning Library", url: "/documents/learning", icon: GraduationCap, show: canViewModule("documents") },
        ],
      },
      {
        id: "finance",
        title: "Finance",
        icon: IndianRupee,
        items: [
          { title: "Patient Billing", url: "/billing", icon: IndianRupee, show: privilegedAccess.canViewBilling && canViewModule("billing") },
          { title: "Memberships", url: "/memberships", icon: Crown, show: privilegedAccess.canViewBilling && canViewModule("billing") },
          { title: "Bill Reconciliation", url: "/bill-reconciliation", icon: FileCheck, show: privilegedAccess.canViewBilling && canViewModule("billing") },
          { title: "Due Bills", url: "/billing/collections", icon: Wallet, show: privilegedAccess.canCollectOperationalDues && canViewModule("dues_tracker") },
          { title: "EMI Tracking", url: "/emi-tracking", icon: Receipt, show: canViewModule("emi_tracking") },
          { title: "Travel Claims", url: "/travel-reimbursement", icon: Car, show: true, featureCode: "travel_reimbursement", requiredPlan: "Professional" },
          { title: "Expense Tracker", url: "/vendor-invoices", icon: FileText, show: canViewModule("vendor_invoice_management") },
          { title: "Price Chart", url: "/price-chart", icon: DollarSign, show: canViewModule("price_chart") },
          { title: "Approval Queue", url: "/approvals", icon: Shield, show: hasManagerAccess, badge: approvalCount > 0 ? approvalCount : undefined },
        ],
      },
      {
        id: "logbooks",
        title: "Logbooks",
        icon: Clock,
        items: [
          { title: "Assistant Logbook", url: "/assistant-logbook", icon: Clock, show: canViewModule("assistant_logbook") },
          { title: "Call Logbook", url: "/call-logbook", icon: Phone, show: canViewModule("call_logbook") },
          { title: "Consultant Logbook", url: "/consultant-logbook", icon: Clock, show: canViewModule("consultant_logbook") },
          { title: "Equipment Logbook", url: "/equipment-logbook", icon: Wrench, show: canViewModule("equipment_logbook") },
        ],
      },
      {
        id: "audit-trail",
        title: "Audit Trail",
        icon: Eye,
        items: [
          { title: "Phone Access", url: "/audit/phone-access", icon: Phone, show: canViewModule("audit_trail") },
          { title: "User Activity", url: "/audit/user-activity", icon: Users, show: canViewModule("audit_trail") },
          { title: "Patient Records", url: "/audit/patient-records", icon: FileText, show: canViewModule("audit_trail") },
          { title: "Clinical Records", url: "/audit/clinical-records", icon: Stethoscope, show: canViewModule("audit_trail") },
          { title: "Financial", url: "/audit/financial", icon: IndianRupee, show: canViewModule("financial_audit") },
          { title: "Reconciliation", url: "/bill-reconciliation-audit", icon: FileCheck, show: hasFullAccess },
          { title: "Rewards", url: "/audit/rewards", icon: Gift, show: canViewModule("financial_audit") },
          { title: "Appointments", url: "/audit/appointments", icon: Calendar, show: canViewModule("audit_trail") },
          { title: "Inventory", url: "/audit/inventory", icon: Package, show: canViewModule("audit_trail") },
        ],
      },
      {
        id: "clinical-audit",
        title: "Clinical Audit",
        icon: ShieldCheck,
        items: [
          { title: "Treatment Planning", url: "/clinical-audit/treatment-planning", icon: ClipboardList, show: canViewModule("clinical_audit") },
          { title: "Treatment Outcome", url: "/clinical-audit/treatment-outcome", icon: FileCheck, show: canViewModule("clinical_audit") },
          { title: "Clinic Quality", url: "/clinical-audit/clinic-quality", icon: ShieldCheck, show: canViewModule("clinical_audit") },
        ],
      },
      {
        id: "reports",
        title: "Reports",
        icon: BarChart3,
        items: [
          { title: "MIS Reports", url: "/reports/mis", icon: BarChart3, show: canViewModule("reports") },
          { title: "Patient Feedback", url: "/patient-feedback", icon: Smile, show: hasManagerAccess },
          { title: "Doctor Payouts", url: "/reports/doctor-payouts", icon: Calculator, show: canViewModule("reports") },
        ],
      },
      {
        id: "organization",
        title: "Organization",
        icon: Building2,
        items: [
          { title: "Subscription", url: "/organization/subscription", icon: Receipt, show: hasFullAccess },
          { title: "Support", url: "/organization/support", icon: LifeBuoy, show: hasFullAccess },
          { title: "Announcements", url: "/organization/announcements", icon: Megaphone, show: hasFullAccess },
        ],
      },
      {
        id: "administration",
        title: "Administration",
        icon: Settings,
        items: [
          { title: "System Management", url: "/admin/system", icon: Users, show: canViewModule("settings_management") },
          { title: "Clinical Config", url: "/admin/clinical", icon: Stethoscope, show: canViewModule("settings_clinical") },
          { title: "Lab Management", url: "/admin/lab", icon: FlaskConical, show: canViewModule("settings_lab") },
          { title: "Radiology Config", url: "/admin/radiology", icon: Scan, show: canViewModule("settings_radiology"), featureCode: "radiology", requiredPlan: "Professional" },
          { title: "Integrations", url: "/admin/integrations", icon: Boxes, show: canViewModule("settings_integrations") },
          { title: "Payout Configuration", url: "/admin/payouts", icon: Calculator, show: canViewModule("settings_payouts") },
          { title: "Consent Templates", url: "/admin/consent-templates", icon: FileCheck, show: canViewModule("consent_forms") },
        ],
      },
    ];
  }, [
    standardRole,
    modulePermissions,
    pendingApprovals,
    userClinics,
    privilegedAccess.canViewBilling,
    privilegedAccess.canCollectOperationalDues,
    privilegedAccess.canViewTreatmentPlans,
  ]);

  // Filter categories to only show ones with visible items
  const visibleCategories = useMemo(() => {
    return navigationCategories
      .map(category => ({
        ...category,
        items: category.items.filter(item => item.show),
      }))
      .filter(category => category.items.length > 0);
  }, [navigationCategories]);

  // Determine which group should be open based on current route
  const getActiveGroupId = (): string | null => {
    for (const category of visibleCategories) {
      for (const item of category.items) {
        if (isActiveRoute(item.url)) {
          return category.id;
        }
      }
    }
    return null;
  };

  const activeGroupId = getActiveGroupId();

  // Track open groups - initialize with active group open
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (activeGroupId) initial.add(activeGroupId);
    return initial;
  });

  // Update open groups when route changes
  useEffect(() => {
    if (activeGroupId && !openGroups.has(activeGroupId)) {
      setOpenGroups(prev => new Set([...prev, activeGroupId]));
    }
  }, [activeGroupId, location.pathname]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Sidebar collapsible="icon" className="border-r border-sidebar-border">
        <SidebarHeader className="border-b border-sidebar-border p-1">
          <div className="flex items-center justify-center">
            <DentalFlowLogo size={isCollapsed ? "sm" : "lg"} />
          </div>
        </SidebarHeader>

        <SidebarContent className="px-2 py-2">
          {visibleCategories.map((category) => {
            const isGroupOpen = openGroups.has(category.id);
            const hasActiveItem = category.items.some(item => isActiveRoute(item.url));
            const CategoryIcon = category.icon;

            // For single item categories (like Overview), render directly
            if (category.items.length === 1) {
              const item = category.items[0];
              const ItemIcon = item.icon;
              const isActive = isActiveRoute(item.url);

              return (
                <SidebarGroup key={category.id} className="py-0.5">
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            className="h-10"
                          >
                            <NavLink to={item.url} end={item.url === "/"}>
                              <ItemIcon className="h-5 w-5 shrink-0" />
                              {!isCollapsed && <span>{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right" className="font-medium">
                            {item.title}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroup>
              );
            }

            // For multi-item categories, render as collapsible groups
            return (
              <Collapsible
                key={category.id}
                open={isCollapsed ? false : isGroupOpen}
                onOpenChange={() => !isCollapsed && toggleGroup(category.id)}
                className="group/collapsible"
              >
                <SidebarGroup className="py-0.5">
                  <SidebarGroupLabel asChild className="h-9 px-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CollapsibleTrigger 
                          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground ${
                            hasActiveItem 
                              ? 'text-sidebar-primary font-semibold' 
                              : 'text-sidebar-foreground/70'
                          }`}
                        >
                          <CategoryIcon className="h-4 w-4 shrink-0" />
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-left">{category.title}</span>
                              <ChevronRight 
                                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                                  isGroupOpen ? 'rotate-90' : ''
                                }`} 
                              />
                            </>
                          )}
                        </CollapsibleTrigger>
                      </TooltipTrigger>
                      {isCollapsed && (
                        <TooltipContent side="right" className="font-medium">
                          {category.title}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </SidebarGroupLabel>
                  
                  <CollapsibleContent>
                    <SidebarGroupContent>
                        <SidebarMenu className="mt-1 ml-3 border-l border-sidebar-border/30 pl-3">
                        {category.items.map((item) => {
                          const isActive = isActiveRoute(item.url);
                          
                          // If item has featureCode, check if locked
                          if (item.featureCode && item.requiredPlan) {
                            return (
                              <SidebarFeatureItemWrapper
                                key={item.url}
                                item={item}
                                isActive={isActive}
                              />
                            );
                          }
                          
                          return (
                            <SidebarMenuItem key={item.url}>
                              <SidebarMenuButton
                                asChild
                                isActive={isActive}
                                size="default"
                                className={`h-9 text-sm transition-all ${
                                  isActive 
                                    ? 'bg-sidebar-primary/10 text-sidebar-primary font-medium border-l-2 border-sidebar-primary -ml-[13px] pl-[11px]' 
                                    : 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/30'
                                }`}
                              >
                                <NavLink to={item.url}>
                                  <span>{item.title}</span>
                                  {item.badge && item.badge > 0 && (
                                    <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                                      {item.badge}
                                    </span>
                                  )}
                                </NavLink>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            );
          })}
        </SidebarContent>

        <SidebarFooter className="border-t border-sidebar-border">
          {profile && (
            <div className="p-2">
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={`w-full transition-all duration-200 hover:bg-sidebar-accent ${
                          isCollapsed ? "h-12 px-0" : "h-auto px-3 py-2"
                        }`}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <Avatar className="h-9 w-9 ring-2 ring-sidebar-primary/20 shrink-0">
                            <AvatarFallback className="bg-gradient-to-br from-sidebar-primary to-sidebar-primary/80 text-sidebar-primary-foreground text-sm font-semibold">
                              {profile.full_name?.charAt(0) || "U"}
                            </AvatarFallback>
                          </Avatar>
                          {!isCollapsed && (
                            <div className="flex-1 min-w-0 text-left">
                              <p className="text-sm font-semibold truncate">{profile.full_name}</p>
                              <p className="text-xs text-sidebar-foreground/60 truncate">
                                {getDisplayRole()}
                              </p>
                            </div>
                          )}
                          {!isCollapsed && <ChevronUp className="h-4 w-4 ml-auto shrink-0" />}
                        </div>
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right">
                      <div className="text-center">
                        <p className="font-semibold">{profile.full_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {getDisplayRole()}
                        </p>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>

                <DropdownMenuContent 
                  align="end" 
                  side="top"
                  className="w-56"
                >
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-semibold">{profile.full_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {getDisplayRole()}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="h-4 w-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  
                  {standardRole?.isOwner && (
                    <DropdownMenuItem onClick={() => navigate("/organization/subscription")}>
                      <Receipt className="h-4 w-4 mr-2" />
                      Subscription
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem onClick={() => navigate("/app-info")}>
                    <Info className="h-4 w-4 mr-2" />
                    App Info
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => openReportDialog()}>
                    <Bug className="h-4 w-4 mr-2" />
                    Report Bug
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    onClick={onSignOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
              </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
        </SidebarFooter>
      </Sidebar>
    </TooltipProvider>
  );
}
